import cx from 'classnames';
import downloadSvg from 'assets/icons/download.svg';
import monitoringSvg from 'assets/icons/monitoring.svg';
import Button, { Color, Size } from 'lib/Button';
import Modal, { ModalSize } from 'lib/Modal';
import Checkbox from 'lib/Checkbox';
import IconButton from 'lib/IconButton';
import { ButtonSize, Theme } from 'lib/IconButton/IconButton.types';
import { getDealIdsStringFromMarkets } from 'utils/dealIdsFromMarkets';
import { usePageFilters } from 'customHooks/usePageFilters';
import { CreativeType } from 'components/common/types';
import useCreativeAdministration from './useCreativeAdministration';
import Filters from './Filters';
import Creatives from './Creatives';
import { CreativeAdministrationPageFilters } from './Filters/Filters.types';

const CreativeInfoItem = ({ label, value }: { label: string; value: string }): React.ReactElement => {
  return (
    <div className="grid grid-cols-2 text-neutral-950-opacity-80">
      <span className="body-base">{label}</span>
      <span className="sub-header-base">{value}</span>
    </div>
  );
};

const CreativeAdministration = (): React.ReactElement => {
  const {
    isDialogOpen,
    selectedCreatives,
    selectedMarkets,
    areMarketsUpdated,
    handleOpenDialog,
    handleCloseDialog,
    handleSelectMarket,
    handleConfirm,
    setSelectedCreatives,
  } = useCreativeAdministration();

  const { filters } = usePageFilters<CreativeAdministrationPageFilters>();

  return (
    <>
      <Filters />
      <Creatives
        onOpenDialog={handleOpenDialog}
        setSelectedCreatives={setSelectedCreatives}
        areMarketsUpdated={areMarketsUpdated}
        hasUserSelectedCreatives={selectedCreatives.length > 0}
        filters={filters}
      />
      <Modal
        dataTestId="assign-market-dialog"
        icon={monitoringSvg}
        title="Assign market"
        isOpen={isDialogOpen}
        size={ModalSize.MEDIUM}
        shouldCloseOnEsc
        actionButtons={
          <>
            <Button color={Color.DANGER} size={Size.LARGE} onClick={handleCloseDialog} label="Cancel" />
            <Button onClick={handleConfirm} size={Size.LARGE} color={Color.PRIMARY} label="Assign market" />
          </>
        }
        sidebar={
          selectedCreatives.length === 1 ? (
            <div className="bg-neutral-100-opacity-50 min-h-full py-8 px-12 space-y-7 flex flex-col rounded-br-xl basis-[250px]">
              <div className="relative">
                <img
                  className="rounded-2xl justify-self-center w-full h-auto"
                  src={selectedCreatives[0].thumbnail}
                  alt={selectedCreatives[0].externalId}
                />
                {selectedCreatives[0].type === CreativeType.DYNAMIC && (
                  <div className="flex justify-center">
                    <a href={selectedCreatives[0].downloadURL}>
                      <IconButton
                        name="download-creative"
                        icon={downloadSvg}
                        theme={Theme.TERTIARY}
                        buttonSize={ButtonSize.MEDIUM}
                      />
                    </a>
                  </div>
                )}
              </div>
              <div className="space-y-2.5 break-words">
                <CreativeInfoItem label="Creative ID" value={selectedCreatives[0].externalId} />
                <CreativeInfoItem label="DSP" value={selectedCreatives[0].dsp.name} />
                <CreativeInfoItem label="Deal ID" value={getDealIdsStringFromMarkets(selectedCreatives[0].market)} />
              </div>
            </div>
          ) : null
        }
      >
        <div className="-mx-8 px-12 py-3 border-t border-b border-neutral-100-opacity-10">
          {selectedMarkets.length === 0 ? (
            <p className="ml-4 body-base text-neutral-950-opacity-60">
              There are no common DSP markets for the selected creatives. Please review selected creatives
            </p>
          ) : (
            <ul className="grid grid-cols-1 gap-2">
              {selectedMarkets.map(({ market, isSelected }) => {
                return (
                  <li key={market} className={cx({ 'bg-neutral-950-opacity-5': isSelected })}>
                    <Checkbox
                      value={market}
                      label={<p className="inline ml-2 body-base text-neutral-950-opacity-80">{market}</p>}
                      isSelected={isSelected}
                      name="market"
                      onChangeWithValue={handleSelectMarket}
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </Modal>
    </>
  );
};

export default CreativeAdministration;
